"use client"

import { useState, useEffect, useOptimistic, useTransition } from "react"
import { useUser } from "@clerk/nextjs"
import { UserButton } from "@clerk/nextjs"
import { ZoneSelector } from "@/components/zone-selector"
import { ZoneDetail } from "@/components/zone-detail"
import { HistoryView } from "@/components/history-view"
import { ReflectionMessage } from "@/components/reflection-message"
import { AboutModal } from "@/components/about-modal"
import { MigrationModal } from "@/components/migration-modal"
import { ErrorMessage } from "@/components/error-message"
import { EditEntryDialog } from "@/components/edit-entry-dialog"
import { DeleteEntryDialog } from "@/components/delete-entry-dialog"
import { DeleteGratitudeDialog } from "@/components/delete-gratitude-dialog"
import { ExportMenu } from "@/components/export-menu"
import { StreakBadge } from "@/components/streak-badge"
import { StreakMilestoneDialog } from "@/components/streak-milestone-dialog"
import { ReminderBanner } from "@/components/reminder-banner"
import { GratitudeChallenge } from "@/components/gratitude-challenge"
import { PushPermissionPrompt } from "@/components/push-permission-prompt"
import { SentimentInsightCard } from "@/components/sentiment-insight-card"
import { getZoneById } from "@/lib/zones"
import { hasLocalStorageData } from "@/lib/migration"
import { getPendingEntry, clearPendingEntry } from "@/lib/pending-entry"
import { formatCzechDate } from "@/lib/date-utils"
import { getTodayInPrague, getTimestampInPrague } from "@/lib/timezone"
import { updateAppBadge } from "@/lib/badge"
import { cleanOldPatternCaches } from "@/lib/cache-utils"
import { triggerIOSWizard } from "@/lib/wizard-trigger"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { Sparkles, Info, Loader2, CheckCircle2, Bell } from "lucide-react"
import { motion } from "framer-motion"
import { useRouter } from "next/navigation"

import type { DailyEntry as DBDailyEntry, GratitudeEntry } from "@/lib/db/schema"

type View = "selector" | "detail" | "saved"

// Use database type for consistency
type DailyEntry = DBDailyEntry

export default function Home() {
  const router = useRouter()
  const { isLoaded, isSignedIn, user } = useUser()
  const [view, setView] = useState<View>("selector")
  const [selectedZoneId, setSelectedZoneId] = useState<string>()
  const [entries, setEntries] = useState<DailyEntry[]>([])
  const [todayEntry, setTodayEntry] = useState<DailyEntry>()
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [showAbout, setShowAbout] = useState(false)
  const [showMigration, setShowMigration] = useState(false)
  const [migrationChecked, setMigrationChecked] = useState(false)
  const [fetchError, setFetchError] = useState<string | null>(null)
  const [isNewUserSetup, setIsNewUserSetup] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)
  const [editingEntry, setEditingEntry] = useState<DailyEntry | null>(null)
  const [deletingEntry, setDeletingEntry] = useState<DailyEntry | null>(null)
  const [currentStreak, setCurrentStreak] = useState(0)
  const [longestStreak, setLongestStreak] = useState(0)
  const [milestoneReached, setMilestoneReached] = useState<number | null>(null)
  const [pendingEntrySaved, setPendingEntrySaved] = useState(false)
  const [gratitudeEntries, setGratitudeEntries] = useState<GratitudeEntry[]>([])
  const [deletingGratitude, setDeletingGratitude] = useState<GratitudeEntry | null>(null)

  // Computed: check if user is anonymous (not signed in)
  const isAnonymous = !isSignedIn

  // Helper function to fetch with retry logic for new users
  // This is defined early so it can be used in useEffect hooks
  const fetchWithRetry = async (url: string, retries = 3): Promise<Response> => {
    for (let i = 0; i < retries; i++) {
      const res = await fetch(url)

      // If successful, return immediately
      if (res.ok) return res

      // If 401 error and we have retries left, wait and retry
      // This handles the race condition where the user isn't in the database yet
      if (res.status === 401 && i < retries - 1) {
        const delay = 1000 * (i + 1) // 1s, 2s, 3s
        console.log(`Retrying ${url} in ${delay}ms (attempt ${i + 1}/${retries})`)
        await new Promise(resolve => setTimeout(resolve, delay))
        continue
      }

      // For other errors or final retry, return the response
      return res
    }

    // This should never happen, but TypeScript needs it
    throw new Error("Max retries exceeded")
  }

  // Optimistic updates for entries
  const [optimisticEntries, addOptimisticEntry] = useOptimistic(
    entries,
    (state, newEntry: DailyEntry) => {
      // For updates: replace existing entry with same date
      const existingIndex = state.findIndex((e) => e.date === newEntry.date)
      if (existingIndex >= 0) {
        const updated = [...state]
        updated[existingIndex] = newEntry
        return updated
      }
      // For new entries: add to the beginning
      return [newEntry, ...state]
    }
  )
  const [isPending, startTransition] = useTransition()

  // Check for migration need when user is authenticated
  useEffect(() => {
    if (!isSignedIn || migrationChecked) {
      return
    }

    // Clean old pattern caches on app mount (once per session)
    cleanOldPatternCaches()

    const checkMigration = async () => {
      try {
        // Check if user has migration completed preference (with retry for new users)
        const prefsRes = await fetchWithRetry("/api/user/preferences")
        if (prefsRes.ok) {
          const { preferences } = await prefsRes.json()

          // If migration not completed and localStorage has data, show migration modal
          if (!preferences.migrationCompleted && hasLocalStorageData()) {
            setShowMigration(true)
          }

          // Auto-detect and sync timezone
          const detectedTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone
          if (detectedTimezone && preferences.timezone !== detectedTimezone) {
            console.log(`[Timezone] Auto-detected timezone change: ${preferences.timezone} → ${detectedTimezone}`)
            // Update timezone in background (don't await)
            fetch("/api/user/preferences", {
              method: "PATCH",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ timezone: detectedTimezone }),
            }).catch((err) => console.error("[Timezone] Failed to update:", err))
          }
        }
      } catch (error) {
        console.error("Error checking migration status:", error)
      } finally {
        setMigrationChecked(true)
      }
    }

    checkMigration()
  }, [isSignedIn, migrationChecked, fetchWithRetry])

  // Fetch entries and today's entry from API
  useEffect(() => {
    if (!isSignedIn) {
      setLoading(false)
      return
    }

    const fetchData = async () => {
      try {
        setLoading(true)
        setFetchError(null)
        setIsNewUserSetup(false)

        // Fetch all entries (with retry for new users)
        const entriesRes = await fetchWithRetry("/api/entries")
        if (!entriesRes.ok) {
          // If this is a 401 error after retries, it might be a new user setup issue
          if (entriesRes.status === 401) {
            setIsNewUserSetup(true)
            throw new Error("Nastavujeme tvůj účet... Zkus to prosím za chvíli znovu.")
          }
          throw new Error("Nepodařilo se načíst záznamy")
        }
        const entriesData = await entriesRes.json()
        setEntries(entriesData.entries || [])

        // Fetch today's entry (with retry for new users)
        let fetchedTodayEntry: DailyEntry | undefined
        const todayRes = await fetchWithRetry("/api/entries/today")
        if (todayRes.ok) {
          const data = await todayRes.json()
          fetchedTodayEntry = data.entry || undefined
          setTodayEntry(fetchedTodayEntry)
        }

        // Fetch current streak (with retry for new users)
        const streakRes = await fetchWithRetry("/api/user/streak")
        if (streakRes.ok) {
          const streakData = await streakRes.json()
          setCurrentStreak(streakData.streak?.currentStreak || 0)
          setLongestStreak(streakData.streak?.longestStreak || 0)
        }

        // Fetch gratitude entries
        const gratitudeRes = await fetchWithRetry("/api/gratitude")
        if (gratitudeRes.ok) {
          const gratitudeData = await gratitudeRes.json()
          setGratitudeEntries(gratitudeData.entries || [])
        }

        // Update app badge based on today's entry
        updateAppBadge(fetchedTodayEntry)
      } catch (error) {
        console.error("Error fetching data:", error)
        const message = error instanceof Error ? error.message : "Nepodařilo se načíst data"
        setFetchError(message)
      } finally {
        setLoading(false)
      }
    }

    fetchData()
  }, [isSignedIn])

  // Handle pending entry after user authenticates (conversion funnel)
  useEffect(() => {
    // Only run for newly authenticated users
    if (!isSignedIn || !user || loading) return

    const pending = getPendingEntry()
    if (!pending) return

    // Auto-save the pending entry
    const savePendingEntryToDb = async () => {
      try {
        const today = getTodayInPrague()
        const timestamp = getTimestampInPrague()

        const response = await fetch("/api/entries", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            date: today,
            zoneId: pending.zoneId,
            note: pending.note || undefined,
            timestamp: timestamp,
          }),
        })

        if (!response.ok) {
          throw new Error("Nepodařilo se uložit záznam")
        }

        const data = await response.json()
        const newEntry = data.entry

        // Clear pending entry from sessionStorage
        clearPendingEntry()

        // Update state with the new entry
        setTodayEntry(newEntry)
        setSelectedZoneId(pending.zoneId)
        setPendingEntrySaved(true)

        // Navigate to saved view
        setView("saved")

        // Refresh entries list
        const entriesRes = await fetch("/api/entries")
        if (entriesRes.ok) {
          const entriesData = await entriesRes.json()
          setEntries(entriesData.entries || [])
        }

        // Refresh streak
        const streakRes = await fetch("/api/user/streak")
        if (streakRes.ok) {
          const streakData = await streakRes.json()
          setCurrentStreak(streakData.streak?.currentStreak || 0)
          setLongestStreak(streakData.streak?.longestStreak || 0)
        }
      } catch (error) {
        console.error("Failed to save pending entry:", error)
        // Don't clear pending entry on error - user can retry
      }
    }

    savePendingEntryToDb()
  }, [isSignedIn, user, loading])

  const handleRetryFetch = () => {
    if (!isSignedIn) return

    const fetchData = async () => {
      try {
        setLoading(true)
        setFetchError(null)

        const entriesRes = await fetchWithRetry("/api/entries")
        if (!entriesRes.ok) {
          throw new Error("Nepodařilo se načíst záznamy")
        }
        const entriesData = await entriesRes.json()
        setEntries(entriesData.entries || [])

        const todayRes = await fetchWithRetry("/api/entries/today")
        if (todayRes.ok) {
          const data = await todayRes.json()
          setTodayEntry(data.entry || undefined)
        }
      } catch (error) {
        console.error("Error fetching data:", error)
        const message = error instanceof Error ? error.message : "Nepodařilo se načíst data"
        setFetchError(message)
      } finally {
        setLoading(false)
      }
    }

    fetchData()
  }

  const handleSelectZone = (zoneId: string) => {
    setSelectedZoneId(zoneId)
    setView("detail")
  }

  const handleSave = async (note?: string) => {
    if (!selectedZoneId) return

    // For anonymous users, the ZoneDetail component handles the redirect
    // This function will only be called for authenticated users
    if (!isSignedIn || !user) return

    // Note: Reflection cache will naturally invalidate when zone or date changes
    // No need to clear manually - this reduces unnecessary API calls

    // Create optimistic entry immediately (Czech timezone)
    const now = new Date()
    const today = getTodayInPrague()
    const timestamp = getTimestampInPrague()
    const optimisticEntry: DailyEntry = {
      id: `temp-${Date.now()}`, // Temporary ID
      userId: user.id,
      date: today,
      zoneId: selectedZoneId,
      note: note || null,
      timestamp: timestamp,
      // Sentiment fields (null until analyzed)
      sentimentData: null,
      valence: null,
      arousal: null,
      stressLevel: null,
      sentimentAnalyzedAt: null,
      createdAt: now,
      updatedAt: now,
    }

    // Apply optimistic update and navigate to saved view immediately
    startTransition(() => {
      addOptimisticEntry(optimisticEntry)
      setTodayEntry(optimisticEntry)
      setView("saved")
    })

    try {
      setSaving(true)
      setSaveError(null)

      // Actual API call - use POST to create new entry (supports multiple per day)
      const response = await fetch("/api/entries", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          date: today,
          zoneId: selectedZoneId,
          note: note || undefined,
          timestamp: timestamp,
        }),
      })

      if (!response.ok) {
        throw new Error("Nepodařilo se uložit záznam")
      }

      const data = await response.json()
      const newEntry = data.entry

      // Update with real data from server
      setTodayEntry(newEntry)

      // Refresh entries list and streak from server
      const [entriesRes, streakRes] = await Promise.all([
        fetch("/api/entries"),
        fetch("/api/user/streak")
      ])

      if (entriesRes.ok) {
        const entriesData = await entriesRes.json()
        setEntries(entriesData.entries || [])
      }

      if (streakRes.ok) {
        const streakData = await streakRes.json()
        const newStreak = streakData.streak?.currentStreak || 0
        const prevStreak = currentStreak

        setCurrentStreak(newStreak)
        setLongestStreak(streakData.streak?.longestStreak || 0)

        // Check if we reached a milestone (7, 30, 100, 365 days)
        // Match milestones with dialog messages in StreakMilestoneDialog
        const milestones = [7, 30, 100, 365]
        const reachedMilestone = milestones.find(m => newStreak >= m && prevStreak < m)
        if (reachedMilestone) {
          setMilestoneReached(reachedMilestone)
        }
      }

      // Update app badge after saving entry
      updateAppBadge(newEntry)

      // Clear all service worker notifications
      if ('serviceWorker' in navigator && 'getNotifications' in ServiceWorkerRegistration.prototype) {
        try {
          const registration = await navigator.serviceWorker.ready
          const notifications = await registration.getNotifications()
          notifications.forEach(notification => notification.close())
        } catch (error) {
          console.warn('Failed to clear notifications:', error)
        }
      }

      // Trigger iOS install wizard 10 seconds after successful save
      triggerIOSWizard(10000)
    } catch (error) {
      // On error, optimistic update is automatically rolled back
      console.error("Error saving entry:", error)
      const message = error instanceof Error ? error.message : "Nepodařilo se uložit záznam"
      setSaveError(message)
      // Return to detail view so user can retry
      setView("detail")
    } finally {
      setSaving(false)
    }
  }

  const handleBack = () => {
    setView("selector")
    setSelectedZoneId(undefined)
  }

  const handleEditEntry = async (entryId: string, zoneId: string, note: string | null) => {
    try {
      const response = await fetch(`/api/entries/${entryId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ zoneId, note }),
      })

      if (!response.ok) {
        throw new Error("Nepodařilo se upravit záznam")
      }

      const data = await response.json()
      const updatedEntry = data.entry

      // Update entries list
      setEntries((prev) =>
        prev.map((e) => (e.id === updatedEntry.id ? updatedEntry : e))
      )

      // Update today's entry if it's the one being edited
      if (todayEntry?.id === updatedEntry.id) {
        setTodayEntry(updatedEntry)
      }
    } catch (error) {
      console.error("Error editing entry:", error)
      throw error
    }
  }

  const handleDeleteEntry = async (entryId: string) => {
    try {
      const response = await fetch(`/api/entries/${entryId}`, {
        method: "DELETE",
      })

      if (!response.ok) {
        throw new Error("Nepodařilo se smazat záznam")
      }

      // Remove from entries list
      setEntries((prev) => prev.filter((e) => e.id !== entryId))

      // Clear today's entry if it's the one being deleted
      if (todayEntry?.id === entryId) {
        setTodayEntry(undefined)
      }
    } catch (error) {
      console.error("Error deleting entry:", error)
      throw error
    }
  }

  const handleDeleteGratitude = async (entryId: string) => {
    try {
      const response = await fetch(`/api/gratitude?id=${entryId}`, {
        method: "DELETE",
      })

      if (!response.ok) {
        throw new Error("Nepodařilo se smazat záznam vděčnosti")
      }

      // Remove from gratitude entries list
      setGratitudeEntries((prev) => prev.filter((e) => e.id !== entryId))
    } catch (error) {
      console.error("Error deleting gratitude entry:", error)
      throw error
    }
  }

  const handleMigrationComplete = async () => {
    // Mark migration as completed in user preferences
    try {
      await fetch("/api/user/preferences", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ migrationCompleted: true }),
      })

      // Refresh entries after migration
      const entriesRes = await fetch("/api/entries")
      if (entriesRes.ok) {
        const data = await entriesRes.json()
        setEntries(data.entries || [])
      }

      const todayRes = await fetch("/api/entries/today")
      if (todayRes.ok) {
        const data = await todayRes.json()
        setTodayEntry(data.entry || undefined)
      }
    } catch (error) {
      console.error("Error updating migration status:", error)
    }
  }

  const getTodayFormatted = () => {
    return formatCzechDate(new Date())
  }

  const selectedZone = selectedZoneId ? getZoneById(selectedZoneId) : undefined

  // Show loading state while checking auth (only for authenticated users loading data)
  if (!isLoaded || (isSignedIn && loading)) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    )
  }

  // Note: Anonymous users can now browse the app - no early return here

  return (
    <div className="min-h-screen bg-background pb-safe">
      <div className="container max-w-2xl mx-auto px-3 sm:px-4 py-3 sm:py-6">
        <motion.header
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-4 sm:mb-6"
        >
          {/* Mobile Layout (< 640px) - Minimal header with bottom nav */}
          <div className="sm:hidden">
            <div className="flex items-center justify-between">
              {/* Left: App icon + name */}
              <div className="flex items-center gap-2">
                <Sparkles className="h-5 w-5 text-primary" />
                <h1 className="text-xl font-bold">Vnitřní Mapa</h1>
              </div>

              {/* Right: Auth buttons (anonymous) or avatar (authenticated) */}
              {isAnonymous ? (
                <div className="flex items-center gap-2">
                  <Button asChild size="sm" className="min-h-[36px]">
                    <a href="/sign-in">Přihlásit se</a>
                  </Button>
                  <Button asChild variant="outline" size="sm" className="min-h-[36px]">
                    <a href="/sign-up">Registrace</a>
                  </Button>
                </div>
              ) : (
                <UserButton
                  afterSignOutUrl="/"
                  userProfileMode="navigation"
                  userProfileUrl="/account"
                />
              )}
            </div>
          </div>

          {/* Desktop Layout (≥ 640px) */}
          <div className="hidden sm:block">
            <div className="flex items-center justify-between">
              {/* Left: Title */}
              <div className="flex items-center gap-2">
                <Sparkles className="h-6 w-6 text-primary" />
                <h1 className="text-2xl font-bold">Vnitřní Mapa</h1>
              </div>

              {/* Right: Action Icons (authenticated) or Auth buttons (anonymous) */}
              <div className="flex items-center gap-2">
                {isAnonymous ? (
                  <>
                    <Button asChild size="sm" className="min-h-[36px]">
                      <a href="/sign-in">Přihlásit se</a>
                    </Button>
                    <Button asChild variant="outline" size="sm" className="min-h-[36px]">
                      <a href="/sign-up">Registrace</a>
                    </Button>
                  </>
                ) : (
                  <>
                    <ExportMenu />
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => router.push("/settings")}
                      className="h-9 w-9 rounded-full"
                      aria-label="Nastavení notifikací"
                    >
                      <Bell className="h-5 w-5" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => setShowAbout(true)}
                      className="h-9 w-9 rounded-full"
                      aria-label="O aplikaci"
                    >
                      <Info className="h-5 w-5" />
                    </Button>
                    <UserButton
                      afterSignOutUrl="/"
                      userProfileMode="navigation"
                      userProfileUrl="/account"
                    />
                  </>
                )}
              </div>
            </div>
          </div>
        </motion.header>

        {view === "selector" && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-3 sm:space-y-4">
            {/* Error message - only for authenticated users */}
            {!isAnonymous && fetchError && (
              <ErrorMessage
                title={isNewUserSetup ? "Vítej v aplikaci!" : "Něco se pokazilo"}
                message={fetchError}
                onRetry={handleRetryFetch}
                retrying={loading}
              />
            )}

            {/* Reminder banner - only for authenticated users */}
            {!isAnonymous && <ReminderBanner todayEntry={todayEntry} currentStreak={currentStreak} />}

            <div className="bg-gradient-to-br from-primary/5 to-secondary/5 rounded-2xl p-3 sm:p-5 border border-primary/10">
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <p className="text-xs sm:text-sm text-muted-foreground font-medium uppercase tracking-wide">
                    {getTodayFormatted()}
                  </p>
                  {/* Streak badge - visible to authenticated users */}
                  {!isAnonymous && currentStreak > 0 && (
                    <StreakBadge
                      currentStreak={currentStreak}
                      longestStreak={longestStreak}
                      hasEntries={entries.length > 0}
                      inThreat={!todayEntry}
                      size="sm"
                    />
                  )}
                </div>
                <h2 className="text-lg sm:text-xl font-semibold text-balance leading-relaxed">
                  {isAnonymous
                    ? "Prozkoumejte svůj vnitřní stav"
                    : todayEntry
                      ? "Změnil se tvůj stav? Můžeš ho aktualizovat"
                      : "Vyber podle dnešního rozpoložení, kde se nacházíš"}
                </h2>
                <p className="text-sm text-muted-foreground text-pretty leading-relaxed">
                  {isAnonymous
                    ? "Vyber zónu, která nejlépe odpovídá tomu, jak se právě teď cítíš. Po registraci můžeš svůj stav sledovat a analyzovat."
                    : todayEntry
                      ? "Stačí kliknout na novou zónu a tvůj dnešní záznam se upraví."
                      : "Není to o tom, kde bys měl být, ale o tom, kde právě teď jsi."}
                </p>
              </div>
            </div>

            {/* Reflection message - only for authenticated users with today's entry */}
            {!isAnonymous && todayEntry && user && <ReflectionMessage zone={getZoneById(todayEntry.zoneId)!} userId={user.id} />}

            {/* Gratitude challenge - only for authenticated users after 18:00 */}
            {!isAnonymous && <GratitudeChallenge />}

            <ZoneSelector onSelectZone={handleSelectZone} selectedZone={isAnonymous ? undefined : todayEntry?.zoneId} />

            {/* History view - only for authenticated users with entries */}
            {!isAnonymous && (optimisticEntries.length > 0 || gratitudeEntries.length > 0) && (
              <div className="mt-6 sm:mt-8">
                <HistoryView
                  entries={optimisticEntries}
                  gratitudeEntries={gratitudeEntries}
                  currentStreak={currentStreak}
                  todayEntry={todayEntry}
                  compact
                  onEdit={(entry) => setEditingEntry(entry)}
                  onDelete={(entry) => setDeletingEntry(entry)}
                  onDeleteGratitude={(entry) => setDeletingGratitude(entry)}
                />
              </div>
            )}

            {/* Mobile features banner - desktop only, authenticated users only - at bottom */}
            {!isAnonymous && (
              <div className="hidden sm:block mt-6">
                <Card className="border-primary/20 bg-gradient-to-br from-primary/5 to-primary/10">
                  <CardContent className="p-4">
                    <div className="flex items-start gap-3">
                      <div className="flex-shrink-0 mt-0.5">
                        <Sparkles className="h-5 w-5 text-primary" />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-semibold text-sm mb-1">Více funkcí na mobilu</h3>
                        <p className="text-sm text-muted-foreground">
                          Nainstaluj si aplikaci na mobil a získej přístup k historii záznamů, AI poznatků a dalším funkcím v mobilní navigaci.
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}
          </motion.div>
        )}

        {view === "detail" && selectedZone && (
          <>
            {saveError && (
              <ErrorMessage
                message={saveError}
                onRetry={() => setSaveError(null)}
              />
            )}
            <ZoneDetail
              zone={selectedZone}
              onBack={handleBack}
              onSave={handleSave}
              saving={saving}
              isAnonymous={isAnonymous}
            />
          </>
        )}

        {view === "saved" && selectedZone && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-3 sm:space-y-4">
            <Card className="bg-primary/5 border-primary/20">
              <CardContent className="pt-4 sm:pt-6 pb-4 sm:pb-6 px-3 sm:px-6">
                <div className="flex items-start gap-2.5 sm:gap-3">
                  <CheckCircle2 className="h-4 w-4 sm:h-5 sm:w-5 text-primary mt-0.5 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs sm:text-sm leading-relaxed text-pretty">
                      {pendingEntrySaved
                        ? "Vítejte! Váš záznam byl úspěšně uložen. Vědomost o sobě je první krok k rovnováze."
                        : "Stav uložen. Vědomost o sobě je první krok k rovnováze."}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
            {/* Show sentiment insight if there's a note and entry has been saved (not optimistic) */}
            {todayEntry?.id && !todayEntry.id.startsWith("temp-") && todayEntry.note && (
              <SentimentInsightCard entryId={todayEntry.id} />
            )}
            <div className="flex flex-col sm:flex-row gap-3">
              <Button variant="outline" className="flex-1 bg-transparent min-h-[44px]" onClick={handleBack}>
                Zpět na přehled
              </Button>
            </div>
          </motion.div>
        )}
      </div>
      <AboutModal open={showAbout} onOpenChange={setShowAbout} />
      <MigrationModal
        open={showMigration}
        onOpenChange={setShowMigration}
        onMigrationComplete={handleMigrationComplete}
      />
      {editingEntry && (
        <EditEntryDialog
          entry={editingEntry}
          open={!!editingEntry}
          onOpenChange={(open) => !open && setEditingEntry(null)}
          onSave={handleEditEntry}
        />
      )}
      {deletingEntry && (
        <DeleteEntryDialog
          entry={deletingEntry}
          open={!!deletingEntry}
          onOpenChange={(open) => !open && setDeletingEntry(null)}
          onDelete={handleDeleteEntry}
        />
      )}
      {milestoneReached && (
        <StreakMilestoneDialog
          open={!!milestoneReached}
          onOpenChange={(open) => !open && setMilestoneReached(null)}
          milestone={milestoneReached}
        />
      )}
      {deletingGratitude && (
        <DeleteGratitudeDialog
          entry={deletingGratitude}
          open={!!deletingGratitude}
          onOpenChange={(open) => !open && setDeletingGratitude(null)}
          onDelete={handleDeleteGratitude}
        />
      )}
      {/* Only show notification prompts for authenticated users */}
      {!isAnonymous && <PushPermissionPrompt />}
    </div>
  )
}
